import * as React from 'react';
import classnames from 'classnames';
import { prefixClassName } from '../_conf';
import { DataCard } from './DataCard';
import type { DataCardField } from './DataCard.interfaces';
import { GridAction, GridActionResult } from 'types';
import CheckboxPrimitive from '../FormComponents/Primitives/CheckboxPrimitive';
import './_styles.scss';
import { i18n } from 'i18n';
import PageSizeSelector from '../Pagination/Buttons/PageSizeSelector';

const clx = prefixClassName('data-card-list');

interface DataCardListProps<T> {
  data: T[];
  actions?: GridAction<T, GridActionResult>[];
  onActionInvoked?: (action: any, item: T) => void;
  showActions?: boolean;
  loadingItems?: T[];
  className?: string;
  canSelectItems?: boolean;
  selectedItems?: T[];
  toggleItemSelection?: (item: T) => void;
  toggleAllSelection?: (selectAll: boolean) => void;
  selectionLogic?: (item: T) => boolean;
  disabledItems?: T[];
  isLoading?: boolean;
  cardFields: (item: T) => DataCardField[];
  tableName?: string;
  pageSize?: number;
  pageSizeHandler?: (option: any) => void;
}

export const DataCardList = <T extends {}>({
  data,
  actions,
  onActionInvoked,
  showActions = true,
  loadingItems = [],
  className,
  canSelectItems = false,
  selectedItems = [],
  toggleItemSelection,
  toggleAllSelection,
  selectionLogic,
  disabledItems = [],
  isLoading = false,
  cardFields,
  tableName,
  pageSize,
  pageSizeHandler,
}: DataCardListProps<T>) => {
  let listData: T[] = [];
  if (Array.isArray(data)) listData = data;
  else if (data != null) listData = [data];

  const isItemSelected = (item: T): boolean => {
    if (selectionLogic) {
      return selectionLogic(item);
    }
    return selectedItems.some((selectedItem) => selectedItem === item);
  };

  const isAllSelected = (): boolean => {
    if (selectionLogic) {
      const selection = listData.filter((item) => !disabledItems.includes(item)).map((item) => selectionLogic(item));
      return selection.length > 0 && !selection.includes(false);
    }
    return selectedItems.length > 0 && selectedItems.length === listData.length - disabledItems.length;
  };

  const addPadding = tableName === 'OrdersList' || tableName === 'PickStatusAllFCs';

  if (!listData || listData.length === 0) {
    return (
      <div
        className={classnames(clx, className, {
          [`${clx}--with-padding`]: addPadding,
        })}
      >
        <div className={`${clx}__empty`}>
          <p>{i18n.t('generic.dataFetchError') as string}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classnames(clx, className, {
        [`${clx}--with-padding`]: addPadding,
      })}
    >
      <div className={`${clx}__header`}>
        {canSelectItems && toggleAllSelection && (
          <div className={`${clx}__select-all`}>
            <CheckboxPrimitive
              name="dataCardSelectAll"
              value={isAllSelected()}
              onChange={(e) => toggleAllSelection(e.target.checked)}
              disabled={isLoading}
            />
            <label htmlFor="dataCardSelectAll" className={`${clx}__select-all-label`}>
              {i18n.t('generic.selectAll') as string}
            </label>
          </div>
        )}
        {!!pageSize && pageSizeHandler && (
          <PageSizeSelector pageSize={pageSize} onSetPageSizeHandler={pageSizeHandler} className="mobile-pagesize" />
        )}
      </div>

      {listData.map((item, index) => (
        <DataCard<T>
          key={`${tableName}-item-${index}`}
          item={item}
          fields={cardFields(item)}
          actions={actions}
          onActionInvoked={onActionInvoked}
          showActions={showActions}
          loadingItems={loadingItems}
          canSelectItems={canSelectItems}
          isSelected={isItemSelected(item)}
          onToggleSelection={toggleItemSelection}
          isLoading={isLoading}
          index={index}
          disabled={disabledItems.includes(item)}
          className={className}
        />
      ))}
    </div>
  );
};
